/**
 * sl-728o.6.1: Demo Tour Session Definition
 *
 * 10 action-driven steps that guide the user through the sidebar,
 * then into the edgeless canvas, ending with concrete next-use ideas.
 */
import { requestNativeWorkspaceAppSidebar } from '@affine/core/modules/streamlain/native-app';
import type { useI18n } from '@affine/i18n';

import { DEMO_TOUR_SESSION_ID } from '../../constants';
import type { TutorialSession } from '../../types';
import {
  isTourElementVisible,
  waitForTourElementVisible,
} from './tour-visibility';
import { createViewSwitchTrigger } from './trigger-factories';

type I18n = ReturnType<typeof useI18n>;

const quickSearchButtonSelector =
  '[data-testid="slider-bar-quick-search-button"]';
const quickSearchPanelSelector = '[data-testid="cmdk-quick-search"]';
const appSidebarWrapperSelector = '[data-testid="app-sidebar-wrapper"]';
const globalNavigationRailSelector = '[data-testid="global-navigation-rail"]';
const mobileRailLauncherSelector =
  'button[aria-controls="global-navigation-rail"]';
const appSidebarExpandButtonSelector =
  '[data-testid="app-sidebar-arrow-button-expand"] button';
const accountAvatarSelector = '[data-testid="sidebar-user-avatar"]';
const accountMenuSelector = '[data-testid="sidebar-user-menu"]';
const workspaceButtonSelector = '[data-testid="global-navigation-docs-button"]';
const workspacePanelSelector = '[data-testid="global-navigation-docs-panel"]';
const workspaceSectionSelector = '[data-testid="sidebar-workspace"]';
const appsSectionSelector = '[data-testid="sidebar-apps"]';
const organizedSectionSelectors = [
  '[data-testid="sidebar-personal"]',
  '[data-testid="sidebar-team-areas"]',
];
const othersSelector = '[data-testid="sidebar-others"]';
const extrasSelector = '[data-testid="global-navigation-extras"]';
const edgelessToolbarSelector = 'edgeless-toolbar-widget';
const edgelessToolbarControlSelector = '.edgeless-toolbar-container';
const toolbarTutorialAttribute = 'data-tutorial-force-visible';
const transitionCursorTestId = 'tutorial-transition-cursor';
const transitionCursorClickTestId = 'tutorial-transition-cursor-click';
const resizePreviewTestId = 'tutorial-resize-preview';
const transitionCursorPersona = { name: 'Max', color: '#0891B2' };
const compactTutorialViewportMaxWidth = 900;
const transitionCursorSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none"><path fill="currentColor" d="M3.03 5.302c-.278-1.606 1.41-2.832 2.851-2.07l13.518 7.14c1.542.815 1.336 3.086-.328 3.609l-5.526 1.736a1.96 1.96 0 0 0-1.01.734L9.17 21.174c-1.012 1.421-3.236.915-3.534-.804z"/></svg>';

type AnimatableElement = HTMLElement & {
  animate?: Element['animate'];
};

interface TransitionCursor {
  el: HTMLDivElement;
}

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function playAnimation(
  element: AnimatableElement,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions
) {
  if (!element.animate) {
    const lastFrame = keyframes[keyframes.length - 1];
    for (const [key, value] of Object.entries(lastFrame)) {
      if (key === 'offset' || key === 'easing' || key === 'composite') {
        continue;
      }
      element.style.setProperty(key, String(value));
    }
    await wait(typeof options.duration === 'number' ? options.duration : 0);
    return;
  }

  const animation = element.animate(keyframes, options);
  await animation.finished.catch(() => undefined);
}

function createTransitionCursor() {
  document.querySelector(`[data-testid="${transitionCursorTestId}"]`)?.remove();

  const cursor = document.createElement('div');
  cursor.dataset.testid = transitionCursorTestId;
  cursor.setAttribute('aria-hidden', 'true');
  cursor.innerHTML = `${transitionCursorSvg}<div style="background:${transitionCursorPersona.color};margin-left:14px;margin-top:-2px;color:white;padding:0 3px;border:1px solid rgba(0,0,0,0.15);box-shadow:0 1px 4px rgba(0,0,0,0.12);border-radius:4px;font-size:11px;line-height:16px;white-space:nowrap">${transitionCursorPersona.name}</div>`;
  cursor.style.position = 'fixed';
  cursor.style.left = '0';
  cursor.style.top = '0';
  cursor.style.color = transitionCursorPersona.color;
  cursor.style.zIndex = '3000';
  cursor.style.pointerEvents = 'none';
  cursor.style.opacity = '0';
  cursor.style.transition = 'opacity 0.6s ease';
  cursor.style.willChange = 'transform';
  document.body.append(cursor);
  return { el: cursor };
}

function cleanupTransitionCursorArtifacts() {
  document.querySelector(`[data-testid="${transitionCursorTestId}"]`)?.remove();
  document
    .querySelector(`[data-testid="${transitionCursorClickTestId}"]`)
    ?.remove();
  document.querySelector(`[data-testid="${resizePreviewTestId}"]`)?.remove();
}

function easeCursor(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function setCursorPosition(
  cursor: TransitionCursor,
  x: number,
  y: number,
  scale = 1
) {
  cursor.el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
}

async function moveTransitionCursor(
  cursor: TransitionCursor,
  from: { x: number; y: number },
  to: { x: number; y: number },
  durationMs: number,
  signal?: AbortSignal
) {
  await new Promise<void>(resolve => {
    const start = performance.now();
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    function tick(now: number) {
      if (signal?.aborted) {
        resolve();
        return;
      }
      const rawT = Math.min(1, (now - start) / durationMs);
      const t = easeCursor(rawT);
      setCursorPosition(cursor, from.x + dx * t, from.y + dy * t);
      if (rawT < 1) {
        requestAnimationFrame(tick);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(tick);
  });
}

async function animateSyntheticCursorFocus(
  target: HTMLElement,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();

  const rect = target.getBoundingClientRect();
  const targetX = rect.left + Math.min(rect.width * 0.5, rect.width - 24);
  const targetY = rect.top + Math.min(rect.height * 0.5, rect.height - 24);
  const startX = Math.min(window.innerWidth - 64, Math.max(24, targetX + 190));
  const startY = Math.min(window.innerHeight - 64, Math.max(24, targetY + 110));

  const cursor = createTransitionCursor();
  const cleanup = () => cursor.el.remove();
  signal?.addEventListener('abort', cleanup, { once: true });
  try {
    setCursorPosition(cursor, startX, startY, 0.96);
    await wait(100);
    signal?.throwIfAborted();
    cursor.el.style.opacity = '0.85';
    await moveTransitionCursor(
      cursor,
      { x: startX, y: startY },
      { x: targetX, y: targetY },
      620,
      signal
    );
    signal?.throwIfAborted();

    await Promise.all([
      playAnimation(
        cursor.el,
        [
          {
            opacity: 1,
            transform: `translate(${targetX}px, ${targetY}px) scale(1)`,
          },
          {
            opacity: 1,
            transform: `translate(${targetX}px, ${targetY}px) scale(0.94)`,
          },
          {
            opacity: 1,
            transform: `translate(${targetX}px, ${targetY}px) scale(1)`,
          },
        ],
        {
          duration: 180,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
          fill: 'forwards',
        }
      ),
      playAnimation(
        target,
        [
          { filter: 'brightness(1)' },
          { filter: 'brightness(1.22)' },
          { filter: 'brightness(1)' },
        ],
        {
          duration: 260,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
          fill: 'forwards',
        }
      ),
    ]);

    signal?.throwIfAborted();
    await playAnimation(
      cursor.el,
      [
        {
          opacity: 1,
          transform: `translate(${targetX}px, ${targetY}px) scale(1)`,
        },
        {
          opacity: 0,
          transform: `translate(${targetX}px, ${targetY}px) scale(1.02)`,
        },
      ],
      {
        duration: 160,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
        fill: 'forwards',
      }
    );

    signal?.throwIfAborted();
  } finally {
    signal?.removeEventListener('abort', cleanup);
    cleanup();
  }
}

function focusTargetForTour(resolveTarget: () => HTMLElement | null) {
  return async (signal?: AbortSignal) => {
    const target = await waitForTourElementVisible(resolveTarget, signal, {
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
    await animateSyntheticCursorFocus(target, signal);
  };
}

function dispatchSyntheticPointerActivation(button: HTMLElement) {
  button.dispatchEvent(
    new PointerEvent('pointerdown', {
      bubbles: true,
      button: 0,
      buttons: 1,
      pointerType: 'mouse',
    })
  );
  button.dispatchEvent(
    new PointerEvent('pointerup', {
      bubbles: true,
      button: 0,
      buttons: 0,
      pointerType: 'mouse',
    })
  );
  button.click();
}

async function animateSyntheticCursorClick(
  button: HTMLElement,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();
  button.scrollIntoView({ block: 'center', inline: 'nearest' });
  await wait(80);
  signal?.throwIfAborted();

  const rect = button.getBoundingClientRect();
  const targetX = rect.left + rect.width * 0.5;
  const targetY = rect.top + rect.height * 0.5;
  const startX = Math.min(window.innerWidth - 64, Math.max(24, targetX + 220));
  const startY = Math.min(window.innerHeight - 64, Math.max(24, targetY + 130));

  const cursor = createTransitionCursor();
  const clickRing = document.createElement('div');
  const cleanup = () => {
    cursor.el.remove();
    clickRing.remove();
  };
  signal?.addEventListener('abort', cleanup, { once: true });
  try {
    setCursorPosition(cursor, startX, startY, 0.96);
    await wait(120);
    signal?.throwIfAborted();
    cursor.el.style.opacity = '0.85';
    await moveTransitionCursor(
      cursor,
      { x: startX, y: startY },
      { x: targetX, y: targetY },
      760,
      signal
    );
    signal?.throwIfAborted();

    clickRing.dataset.testid = transitionCursorClickTestId;
    clickRing.style.position = 'fixed';
    clickRing.style.left = `${targetX - 18}px`;
    clickRing.style.top = `${targetY - 18}px`;
    clickRing.style.width = '36px';
    clickRing.style.height = '36px';
    clickRing.style.border = '2px solid rgba(80, 144, 211, 0.95)';
    clickRing.style.borderRadius = '999px';
    clickRing.style.zIndex = '2999';
    clickRing.style.pointerEvents = 'none';
    document.body.append(clickRing);

    await Promise.all([
      playAnimation(
        cursor.el,
        [
          {
            opacity: 1,
            transform: `translate(${targetX}px, ${targetY}px) scale(1)`,
          },
          {
            opacity: 1,
            transform: `translate(${targetX}px, ${targetY}px) scale(0.88)`,
          },
          {
            opacity: 1,
            transform: `translate(${targetX}px, ${targetY}px) scale(1)`,
          },
        ],
        {
          duration: 180,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
          fill: 'forwards',
        }
      ),
      playAnimation(
        clickRing,
        [
          { opacity: 0.9, transform: 'scale(0.45)' },
          { opacity: 0, transform: 'scale(1.25)' },
        ],
        {
          duration: 280,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
          fill: 'forwards',
        }
      ),
      playAnimation(
        button,
        [
          { transform: 'scale(1)', filter: 'brightness(1)' },
          { transform: 'scale(0.96)', filter: 'brightness(1.35)' },
          { transform: 'scale(1)', filter: 'brightness(1)' },
        ],
        {
          duration: 220,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
          fill: 'forwards',
        }
      ),
    ]);
    signal?.throwIfAborted();

    // Radix dropdown triggers open on pointerdown. HTMLElement.click() only
    // emits a click event, which our shared Menu wrapper intentionally consumes.
    // Mirror the real mouse sequence so the visual tutorial click also performs
    // the product action it demonstrates.
    dispatchSyntheticPointerActivation(button);

    await playAnimation(
      cursor.el,
      [
        {
          opacity: 1,
          transform: `translate(${targetX}px, ${targetY}px) scale(1)`,
        },
        {
          opacity: 0,
          transform: `translate(${targetX}px, ${targetY}px) scale(1.02)`,
        },
      ],
      {
        duration: 180,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
        fill: 'forwards',
      }
    );
  } finally {
    signal?.removeEventListener('abort', cleanup);
    cleanup();
  }
}

function isCompactTutorialViewport() {
  return window.innerWidth <= compactTutorialViewportMaxWidth;
}

function closeQuickSearchForTour(closeQuickSearch?: () => void) {
  if (closeQuickSearch) {
    closeQuickSearch();
    return;
  }
  if (!document.querySelector(quickSearchPanelSelector)) {
    return;
  }
  (
    document.querySelector(quickSearchButtonSelector) as HTMLElement | null
  )?.click();
}

async function ensureAppSidebarOpenForTour(options?: DemoTourOptions) {
  const sidebarWrapper = document.querySelector(
    appSidebarWrapperSelector
  ) as HTMLElement | null;
  if (sidebarWrapper?.dataset.staticRail === 'true') {
    const rail = document.querySelector(
      globalNavigationRailSelector
    ) as HTMLElement | null;
    const railVisible = rail && rail.getBoundingClientRect().width > 0;
    if (railVisible) return;

    const launcher = document.querySelector(
      mobileRailLauncherSelector
    ) as HTMLElement | null;
    launcher?.click();
    await wait(260);
    return;
  }
  if (sidebarWrapper?.dataset.open !== 'false') {
    return;
  }

  if (options?.setSidebarOpen) {
    options.setSidebarOpen(true);
  } else {
    const expandButton = document.querySelector(
      appSidebarExpandButtonSelector
    ) as HTMLElement | null;
    expandButton?.click();
  }
  await wait(260);
}

async function ensureCompactSidebarClosedForTour(options?: DemoTourOptions) {
  if (!isCompactTutorialViewport()) {
    return;
  }
  const sidebarWrapper = document.querySelector(
    appSidebarWrapperSelector
  ) as HTMLElement | null;
  if (sidebarWrapper?.dataset.open === 'false') {
    return;
  }
  options?.setSidebarOpen?.(false);
  if (!options?.setSidebarOpen) {
    const collapseButton = document.querySelector(
      '[data-testid="app-sidebar-arrow-button-collapse"] button'
    ) as HTMLElement | null;
    collapseButton?.click();
  }
  await wait(260);
}

function setSidebarSectionExpanded(selector: string, expanded: boolean) {
  const section = document.querySelector(selector) as HTMLElement | null;
  if (!section) {
    return false;
  }
  const toggle = section.querySelector(
    'button[aria-expanded]'
  ) as HTMLButtonElement | null;
  const currentExpanded =
    section.dataset.expanded === 'true' ||
    toggle?.getAttribute('aria-expanded') === 'true';
  if (!toggle || currentExpanded === expanded) {
    return false;
  }
  toggle.click();
  return true;
}

function resolveSidebarSectionTargets(selectors: string[]): Element[] {
  return selectors.flatMap(selector => {
    const section = document.querySelector(selector) as HTMLElement | null;
    if (!section) return [];
    if (!isCompactTutorialViewport()) return [section];
    const header = section.querySelector('[data-clickable-header="true"]');
    return header ? [header] : [section];
  });
}

async function prepareOrganizedSectionsForTour(
  options?: DemoTourOptions,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();
  closeQuickSearchForTour(options?.closeQuickSearch);
  const panel = document.querySelector<HTMLElement>(workspacePanelSelector);
  if (!panel || !isTourElementVisible(panel) || panel.dataset.open !== 'true') {
    await prepareWorkspaceForTour(options, signal);
  }
  signal?.throwIfAborted();
  let changed = false;

  for (const selector of [
    workspaceSectionSelector,
    appsSectionSelector,
    othersSelector,
  ]) {
    changed = setSidebarSectionExpanded(selector, false) || changed;
  }
  for (const selector of organizedSectionSelectors) {
    changed = setSidebarSectionExpanded(selector, true) || changed;
  }

  if (changed) {
    await wait(320);
  }
}

async function prepareWorkspaceForTour(
  options?: DemoTourOptions,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();
  closeQuickSearchForTour(options?.closeQuickSearch);
  await ensureAppSidebarOpenForTour(options);
  signal?.throwIfAborted();

  const button = await waitForElement(workspaceButtonSelector);
  signal?.throwIfAborted();
  if (!(button instanceof HTMLElement)) {
    throw new Error('Tutorial workspace button is unavailable');
  }
  await animateSyntheticCursorClick(button, signal);
  signal?.throwIfAborted();

  // Workspace is a route with its own sidebar, not the old HOME section.
  // Wait for the lazy route before requesting its idempotent open action.
  const panel = await waitForElement(workspacePanelSelector, 10000);
  signal?.throwIfAborted();
  if (!(panel instanceof HTMLElement)) {
    throw new Error('Tutorial workspace panel is unavailable');
  }
  requestNativeWorkspaceAppSidebar('workspace', 'open');
  const deadline = performance.now() + 10000;
  let previousRect: DOMRect | undefined;
  let stableFrames = 0;
  while (performance.now() < deadline) {
    signal?.throwIfAborted();
    if (panel.dataset.open !== 'true') {
      requestNativeWorkspaceAppSidebar('workspace', 'open');
    }
    const rect = panel.getBoundingClientRect();
    const style = getComputedStyle(panel);
    const visible =
      panel.isConnected &&
      panel.dataset.open === 'true' &&
      !panel.closest('[inert]') &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) >= 0.99 &&
      rect.width > 0 &&
      rect.height > 0 &&
      rect.left >= 0 &&
      rect.top >= 0 &&
      rect.right <= window.innerWidth &&
      rect.bottom <= window.innerHeight;
    const settled =
      previousRect &&
      Math.abs(rect.x - previousRect.x) < 0.5 &&
      Math.abs(rect.y - previousRect.y) < 0.5 &&
      Math.abs(rect.width - previousRect.width) < 0.5 &&
      Math.abs(rect.height - previousRect.height) < 0.5;
    stableFrames = visible && settled ? stableFrames + 1 : 0;
    if (stableFrames >= 3) return;
    previousRect = rect;
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
  throw new Error('Tutorial workspace panel did not become visible');
}

async function prepareOthersSectionForTour(
  options?: DemoTourOptions,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();
  closeQuickSearchForTour(options?.closeQuickSearch);
  await ensureAppSidebarOpenForTour(options);
  const extras = await waitForElement(extrasSelector);
  signal?.throwIfAborted();
  if (!(extras instanceof HTMLElement)) {
    throw new Error('Tutorial extras are unavailable');
  }
  extras.scrollIntoView({
    behavior: 'smooth',
    block: 'end',
    inline: 'nearest',
  });
  await waitForTourElementVisible(extrasSelector, signal);
  await animateSyntheticCursorFocus(extras, signal);
}

function cleanupToolbarForTour() {
  document
    .querySelector(edgelessToolbarSelector)
    ?.removeAttribute(toolbarTutorialAttribute);
  cleanupTransitionCursorArtifacts();
}

async function prepareToolbarForTour(signal?: AbortSignal) {
  signal?.addEventListener('abort', cleanupToolbarForTour, { once: true });
  try {
    await focusTargetForTour(() => {
      const toolbar = document.querySelector(edgelessToolbarSelector);
      if (toolbar && !toolbar.hasAttribute(toolbarTutorialAttribute)) {
        toolbar.setAttribute(toolbarTutorialAttribute, 'true');
      }
      return resolveEdgelessToolbarTarget();
    })(signal);
  } catch (error) {
    cleanupToolbarForTour();
    throw error;
  } finally {
    signal?.removeEventListener('abort', cleanupToolbarForTour);
  }
}

function resolveEdgelessToolbarTarget(): HTMLElement | null {
  const toolbar = document.querySelector(
    edgelessToolbarSelector
  ) as HTMLElement | null;
  return (
    (toolbar?.shadowRoot?.querySelector(
      edgelessToolbarControlSelector
    ) as HTMLElement | null) ?? null
  );
}

async function openQuickSearchForTour() {
  if (document.querySelector(quickSearchPanelSelector)) {
    return;
  }
  await ensureAppSidebarOpenForTour();
  const button = document.querySelector(
    quickSearchButtonSelector
  ) as HTMLElement | null;
  if (!button) {
    return;
  }
  await animateSyntheticCursorClick(button);
  await wait(80);
}

function closeAccountMenuForTour() {
  if (!document.querySelector(accountMenuSelector)) {
    return;
  }
  const avatar = document.querySelector(
    accountAvatarSelector
  ) as HTMLElement | null;
  // The outside click on Next may already have closed Radix while its exit
  // animation keeps the menu mounted. Never toggle a closed trigger open.
  if (!avatar || avatar.getAttribute('aria-expanded') !== 'true') {
    return;
  }
  dispatchSyntheticPointerActivation(avatar);
}

async function waitForElement(selector: string, timeoutMs = 1200) {
  const existing = document.querySelector(selector);
  if (existing) {
    return existing;
  }

  return await new Promise<Element | null>(resolve => {
    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (!element) {
        return;
      }
      clearTimeout(timeoutId);
      observer.disconnect();
      resolve(element);
    });
    const timeoutId = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
    observer.observe(document.body, { childList: true, subtree: true });
  });
}

async function prepareAccountMenuForTour(
  options?: DemoTourOptions,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();
  await ensureAppSidebarOpenForTour(options);
  signal?.throwIfAborted();

  const avatar = document.querySelector(
    accountAvatarSelector
  ) as HTMLElement | null;
  if (!avatar) {
    return;
  }

  closeAccountMenuForTour();
  await wait(120);
  await animateSyntheticCursorClick(avatar, signal);
  signal?.throwIfAborted();
  await waitForElement(accountMenuSelector);
  await wait(120);
}

function resolveAccountMenuTargets(): Element[] {
  return [accountAvatarSelector, accountMenuSelector].flatMap(selector => {
    const element = document.querySelector(selector);
    return element ? [element] : [];
  });
}

function resolveQuickSearchTarget(): Element | null {
  return (
    document.querySelector(quickSearchPanelSelector) ??
    document.querySelector(quickSearchButtonSelector)
  );
}

async function prepareQuickSearchForTour(options?: DemoTourOptions) {
  await ensureAppSidebarOpenForTour(options);
  await openQuickSearchForTour();
}

async function prepareDemoDocumentForTour(
  options?: DemoTourOptions,
  signal?: AbortSignal
) {
  signal?.throwIfAborted();
  const docId = options?.getDemoDocId?.();
  if (!docId || !options?.openDemoDoc) {
    throw new Error('Tutorial demo document is unavailable');
  }
  const row = document.querySelector<HTMLElement>(
    `${workspacePanelSelector} [data-testid="navigation-panel-doc-${CSS.escape(docId)}"]`
  );
  const link = row?.querySelector<HTMLAnchorElement>('a[href]');
  if (link && isTourElementVisible(link)) {
    await animateSyntheticCursorClick(link, signal);
  }
  signal?.throwIfAborted();
  // Also normalizes the visible-link path to Page mode without changing the
  // document's saved primary mode; hidden/missing links use this directly.
  await options.openDemoDoc(docId, signal);
  signal?.throwIfAborted();
  await ensureCompactSidebarClosedForTour(options);
  await waitForTourElementVisible('[data-doc-synced="true"]', signal);
}

export interface DemoTourOptions {
  closeQuickSearch?: () => void;
  getDemoDocId?: () => string | undefined;
  openDemoDoc?: (docId: string, signal?: AbortSignal) => Promise<void>;
  /** Called when the tour completes (user clicks Start on tutorial card) */
  onTourComplete?: () => void;
  setSidebarOpen?: (open: boolean) => void;
}

export function createDemoTourSession(
  t: I18n,
  options?: DemoTourOptions
): TutorialSession {
  return {
    id: DEMO_TOUR_SESSION_ID,
    context: {
      category: 'onboarding',
      targetAudience: 'all',
    },
    steps: [
      // Step 1: Account and settings — reveal the menu before the coachmark
      {
        targetResolver: resolveAccountMenuTargets,
        title: t['com.streamlain.streamlain.demo-tour.account.title'](),
        content: t['com.streamlain.streamlain.demo-tour.account.content'](),
        animation: 'highlight',
        onEnter: signal => prepareAccountMenuForTour(options, signal),
        onExit: closeAccountMenuForTour,
        delay: 120,
        trigger: { type: 'manual' },
        position: 'top',
      },
      // Step 2: Quick Search — panel on roomy layouts, trigger on compact layouts
      {
        targetResolver: resolveQuickSearchTarget,
        title: t['com.streamlain.streamlain.demo-tour.search.title'](),
        content: t['com.streamlain.streamlain.demo-tour.search.content'](),
        animation: 'highlight',
        onEnter: () => prepareQuickSearchForTour(options),
        onExit: () => closeQuickSearchForTour(options?.closeQuickSearch),
        delay: 200,
        trigger: { type: 'manual' },
        position: 'left',
      },
      // Step 3: Extras in the static rail, below its divider
      {
        targetSelector: extrasSelector,
        title: t['com.streamlain.streamlain.demo-tour.step3.title'](),
        content: t['com.streamlain.streamlain.demo-tour.step3.content'](),
        animation: 'highlight',
        onEnter: signal => prepareOthersSectionForTour(options, signal),
        onExit: cleanupTransitionCursorArtifacts,
        trigger: { type: 'manual' },
        position: 'right',
      },
      // Step 4: Open Workspace before highlighting its navigation pane
      {
        targetSelector: workspacePanelSelector,
        title: t['com.streamlain.streamlain.demo-tour.home.title'](),
        content: t['com.streamlain.streamlain.demo-tour.home.content'](),
        animation: 'highlight',
        onEnter: signal => prepareWorkspaceForTour(options, signal),
        onExit: cleanupTransitionCursorArtifacts,
        trigger: { type: 'manual' },
        position: 'right',
      },
      // Step 5: Personal and team areas, then open the demo doc in step 6
      {
        targetResolver: () =>
          resolveSidebarSectionTargets(organizedSectionSelectors),
        title: t['com.streamlain.streamlain.demo-tour.organization.title'](),
        content:
          t['com.streamlain.streamlain.demo-tour.organization.content'](),
        animation: 'highlight',
        onEnter: signal => prepareOrganizedSectionsForTour(options, signal),
        trigger: { type: 'manual' },
        position: 'right',
      },
      // Step 6: Open the tutorial document from Team, then explain Page mode.
      {
        title: t['com.streamlain.streamlain.demo-tour.step4.title'](),
        content: t['com.streamlain.streamlain.demo-tour.step4.content'](),
        animation: 'none',
        trigger: { type: 'manual' },
        position: 'auto',
        variant: 'hint',
        hintPosition: 'top-left',
        showScrollIndicator: true,
        onEnter: signal => prepareDemoDocumentForTour(options, signal),
      },
      // Step 7: Switch to Canvas mode.
      {
        targetSelector: '[data-testid="switch-edgeless-mode-button"]',
        title: t['com.streamlain.streamlain.demo-tour.step5.title'](),
        content: t['com.streamlain.streamlain.demo-tour.step5.content'](),
        animation: 'pulse',
        onEnter: async signal => {
          await ensureCompactSidebarClosedForTour(options);
          await waitForTourElementVisible(
            '[data-testid="switch-edgeless-mode-button"]',
            signal,
            { behavior: 'smooth', block: 'nearest', inline: 'nearest' }
          );
        },
        trigger: createViewSwitchTrigger('edgeless'),
        showActionArrow: true,
        fallbackTimeoutMs: 0,
        position: 'bottom',
      },
      // Step 8: Edgeless Toolbar (needs delay — widget mounts after edgeless mode loads)
      {
        targetResolver: resolveEdgelessToolbarTarget,
        title: t['com.streamlain.streamlain.demo-tour.step6.title'](),
        content: t['com.streamlain.streamlain.demo-tour.step6.content'](),
        animation: 'highlight',
        onEnter: prepareToolbarForTour,
        onExit: cleanupToolbarForTour,
        trigger: { type: 'manual' },
        position: 'top',
        delay: 120,
      },
      // Step 9: Zoom & Pan — interactive hint, never dims the canvas
      {
        title:
          t['com.streamlain.streamlain.demo-tour.canvas-navigation.title'](),
        content:
          t['com.streamlain.streamlain.demo-tour.canvas-navigation.content'](),
        mobileContent:
          t[
            'com.streamlain.streamlain.demo-tour.canvas-navigation.mobile-content'
          ](),
        mobileControls: [
          {
            label:
              t[
                'com.streamlain.streamlain.demo-tour.step7.mobile-control.select.label'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step7.mobile-control.select.description'
              ](),
          },
          {
            label:
              t[
                'com.streamlain.streamlain.demo-tour.step7.mobile-control.pan.label'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step7.mobile-control.pan.description'
              ](),
          },
          {
            label:
              t[
                'com.streamlain.streamlain.demo-tour.step7.mobile-control.zoom.label'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step7.mobile-control.zoom.description'
              ](),
          },
        ],
        animation: 'none',
        variant: 'hint',
        hintPosition: 'top-left',
        onEnter: cleanupTransitionCursorArtifacts,
        trigger: { type: 'manual' },
        position: 'auto',
        showNavigationIndicator: true,
      },
      // Step 10: Completion — central summary and next-use ideas
      {
        title: t['com.streamlain.streamlain.demo-tour.step8.title'](),
        content: t['com.streamlain.streamlain.demo-tour.step8.content'](),
        suggestions: [
          {
            title:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.create.title'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.create.description'
              ](),
          },
          {
            title:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.visualize.title'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.visualize.description'
              ](),
          },
          {
            title:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.manage.title'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.manage.description'
              ](),
          },
          {
            title:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.automate.title'
              ](),
            description:
              t[
                'com.streamlain.streamlain.demo-tour.step8.suggestion.automate.description'
              ](),
          },
        ],
        animation: 'none',
        trigger: { type: 'manual' },
        position: 'auto',
        onExit: options?.onTourComplete,
      },
    ],
  };
}
